import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, Typography } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../components/modal/CustomModal";
import { InputReactHookForm } from "../../../components/input/InputReactHookForm";
import CustomSelect, { MenuData } from "../../../components/input/CustomSelect";
import { common, financedEmissions } from "../../../i18n";
import {
  BorrowerLoan,
  LoanSnapshot,
} from "../../../models/dto/cat15/cat15.types";

interface Props {
  open: boolean;
  onClose: () => void;
  loan?: BorrowerLoan;
  snapshot?: LoanSnapshot;
  onSubmit: (values: EditSnapshotForm) => void;
}

export interface EditSnapshotForm {
  outstanding: string;
  tco2e: string;
  dqs: string;
}

const dqsOptions: MenuData[] = [1, 2, 3, 4, 5].map((n) => ({
  value: String(n),
  nameEN: `DQS ${n}`,
  nameTH: `DQS ${n}`,
}));

const EditSnapshotModal = ({
  open,
  onClose,
  loan,
  snapshot,
  onSubmit,
}: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  const { control, handleSubmit, watch, setValue, reset } =
    useForm<EditSnapshotForm>({
      defaultValues: { outstanding: "", tco2e: "", dqs: "3" },
    });

  // Re-seed the form each time the modal opens (the modal stays mounted, so
  // keying on `open` too avoids showing stale values when reopening a row).
  useEffect(() => {
    if (open && snapshot) {
      // dqs may be "excluded" (out-of-calc year); the picker only offers 1–5, so
      // fall back to a valid level in that case.
      const dqsValue =
        snapshot.dqs === "excluded" ? "3" : String(snapshot.dqs);
      reset({
        outstanding: String(snapshot.outstanding),
        tco2e: String(snapshot.tco2e),
        dqs: dqsValue,
      });
    }
  }, [open, snapshot, reset]);

  const dqs = watch("dqs");

  const submit = (values: EditSnapshotForm) => {
    onSubmit(values);
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      maxWidth="480px"
      header={
        <Box>
          <Typography variant="h1">{t("Edit-Snapshot")}</Typography>
          {loan && snapshot && (
            <Typography variant="body2" color="var(--gray9)">
              {loan.loanRef} · {snapshot.year}
            </Typography>
          )}
        </Box>
      }
      body={
        <Box display="flex" flexDirection="column" gap="20px">
          <InputReactHookForm
            required
            name="outstanding"
            control={control}
            label={t("Col-Outstanding")}
            placeholder="0"
            rules={{ required: t("Col-Outstanding") }}
          />
          <Grid container spacing="20px" alignItems="flex-end">
            <Grid xs={12} sm={6}>
              <InputReactHookForm
                disabled
                name="tco2e"
                control={control}
                label="tCO₂e"
                placeholder="0"
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <CustomSelect
                disabled
                label={t("Col-DQS")}
                value={dqs}
                listMenuItem={dqsOptions}
                handleValeOnChange={(e) => setValue("dqs", e.target.value)}
              />
            </Grid>
          </Grid>
          <Typography variant="body2" color="var(--gray9)">
            {t("Snapshot-Engine-Note")}
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap="12px">
            <Button variant="outlined" color="secondary" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSubmit(submit)}
            >
              {t("Save-Changes")}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

export default EditSnapshotModal;
